import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Edit, Maximize2 } from 'lucide-react';
import { 
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  intervalToDays, 
  calculateLaycanStatus 
} from '../../lib/pcerUtils';
import { PivotTableWidget } from './AdvancedPivotTable';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

function processData(reportData, config) {
  if (!reportData || reportData.length === 0) return [];

  const { categoryField, valueField, stackField, aggregationType = 'sum' } = config;

  // Calculate derived fields for each row
  const processedData = reportData.map(row => ({
    ...row,
    port_stay_deviation: row.actual_port_days && row.planned_days 
      ? parseFloat(row.actual_port_days) - parseFloat(row.planned_days) 
      : null,
    laycan_status: calculateLaycanStatus(row.ata, row.laycan_start_date, row.laycan_end_date),
    planned_days: row.planned_days !== null ? parseFloat(row.planned_days) : 
                  (row.working_days && row.idle_days ? 
                    intervalToDays(row.working_days) + intervalToDays(row.idle_days) : null)
  }));

  // Group data by category
  const grouped = {};
  
  processedData.forEach(row => {
    const category = row[categoryField] || 'N/A';
    
    if (!grouped[category]) {
      grouped[category] = { 
        name: category, 
        count: 0, 
        values: {}, 
        aggregateValues: [] 
      };
    }
    
    grouped[category].count++;

    // Handle stacking
    if (stackField) {
      const stackValue = row[stackField] || 'N/A';
      if (!grouped[category].values[stackValue]) {
        grouped[category].values[stackValue] = {
          values: [],
          count: 0
        };
      }
      
      grouped[category].values[stackValue].count++;
      
      if (valueField === '_count') {
        grouped[category].values[stackValue].values.push(1);
      } else {
        const value = parseFloat(row[valueField]);
        if (!isNaN(value)) {
          grouped[category].values[stackValue].values.push(value);
        }
      }
    } else {
      // No stacking, just aggregate values
      if (valueField === '_count') {
        grouped[category].aggregateValues.push(1);
      } else {
        const value = parseFloat(row[valueField]);
        if (!isNaN(value)) {
          grouped[category].aggregateValues.push(value);
        }
      }
    }
  });

  // Apply aggregation function
  const aggregate = (values) => {
    if (values.length === 0) return 0;
    
    switch (aggregationType) {
      case 'sum':
        return values.reduce((sum, v) => sum + v, 0);
      case 'average':
        return values.reduce((sum, v) => sum + v, 0) / values.length;
      case 'count':
        return values.length;
      case 'min':
        return Math.min(...values);
      case 'max':
        return Math.max(...values);
      default:
        return values.reduce((sum, v) => sum + v, 0);
    }
  };

  // Convert to array and apply aggregation
  let result = Object.values(grouped);

  // For stacked charts, flatten the values with aggregation
  if (stackField) {
    result = result.map(item => {
      const processed = { name: item.name };
      Object.keys(item.values).forEach(stackKey => {
        processed[stackKey] = aggregate(item.values[stackKey].values);
      });
      return processed;
    });
  } else {
    result = result.map(item => ({
      name: item.name,
      value: aggregate(item.aggregateValues)
    }));
  }

  return result;
}

function BarChartComponent({ data, config }) {
  const showLegend = config.showLegend !== false;
  
  if (config.stackField) {
    // Stacked bar chart
    const stackKeys = new Set();
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'name') stackKeys.add(key);
      });
    });

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
          <YAxis />
          <Tooltip />
          {showLegend && <Legend />}
          {Array.from(stackKeys).map((key, index) => (
            <Bar key={key} dataKey={key} stackId="a" fill={COLORS[index % COLORS.length]} name={key} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Regular bar chart
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
        <YAxis />
        <Tooltip />
        {showLegend && <Legend />}
        <Bar dataKey="value" fill="#3b82f6" name="Value" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieChartComponent({ data, config }) {
  const showLegend = config.showLegend !== false;
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={80}
          label={(entry) => `${entry.name}: ${entry.value.toFixed(2)}`}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        {showLegend && <Legend />}
      </PieChart>
    </ResponsiveContainer>
  );
}

function PivotTableComponent({ data, config }) {
  const { aggregationType = 'sum', showTotals = true } = config;
  
  const getAggregationLabel = () => {
    switch (aggregationType) {
      case 'sum': return 'Sum';
      case 'average': return 'Avg';
      case 'count': return 'Count';
      case 'min': return 'Min';
      case 'max': return 'Max';
      default: return 'Value';
    }
  };
  
  if (!config.stackField) {
    // Simple summary table
    const total = data.reduce((sum, row) => sum + (row.value || 0), 0);
    
    return (
      <div className="overflow-auto h-full">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left border">{config.categoryField}</th>
              <th className="px-4 py-2 text-right border">{getAggregationLabel()}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{row.name}</td>
                <td className="px-4 py-2 text-right border">{row.value?.toFixed(2) || 0}</td>
              </tr>
            ))}
            {showTotals && data.length > 0 && (
              <tr className="bg-blue-50 font-semibold">
                <td className="px-4 py-2 border">Total</td>
                <td className="px-4 py-2 text-right border">
                  {aggregationType === 'average' 
                    ? (total / data.length).toFixed(2)
                    : total.toFixed(2)
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // Pivot table with stacking
  const stackKeys = new Set();
  data.forEach(item => {
    Object.keys(item).forEach(key => {
      if (key !== 'name') stackKeys.add(key);
    });
  });
  
  const stackKeysArray = Array.from(stackKeys);
  
  // Calculate column totals
  const columnTotals = {};
  stackKeysArray.forEach(key => {
    columnTotals[key] = data.reduce((sum, row) => sum + (row[key] || 0), 0);
  });

  return (
    <div className="overflow-auto h-full">
      <table className="w-full text-sm border-collapse">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-4 py-2 text-left border">{config.categoryField}</th>
            {stackKeysArray.map(key => (
              <th key={key} className="px-4 py-2 text-right border">{key}</th>
            ))}
            {showTotals && <th className="px-4 py-2 text-right border bg-blue-100">Row Total</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const rowTotal = stackKeysArray.reduce((sum, key) => sum + (row[key] || 0), 0);
            return (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{row.name}</td>
                {stackKeysArray.map(key => (
                  <td key={key} className="px-4 py-2 text-right border">
                    {row[key]?.toFixed(2) || 0}
                  </td>
                ))}
                {showTotals && (
                  <td className="px-4 py-2 text-right border font-semibold bg-blue-50">
                    {aggregationType === 'average'
                      ? (rowTotal / stackKeysArray.length).toFixed(2)
                      : rowTotal.toFixed(2)
                    }
                  </td>
                )}
              </tr>
            );
          })}
          {showTotals && data.length > 0 && (
            <tr className="bg-blue-100 font-semibold">
              <td className="px-4 py-2 border">Column Total</td>
              {stackKeysArray.map(key => (
                <td key={key} className="px-4 py-2 text-right border">
                  {aggregationType === 'average'
                    ? (columnTotals[key] / data.length).toFixed(2)
                    : columnTotals[key].toFixed(2)
                  }
                </td>
              ))}
              {showTotals && (
                <td className="px-4 py-2 text-right border bg-blue-200">
                  {aggregationType === 'average'
                    ? (
                        stackKeysArray.reduce((sum, key) => sum + columnTotals[key], 0) / 
                        (stackKeysArray.length * data.length)
                      ).toFixed(2)
                    : stackKeysArray.reduce((sum, key) => sum + columnTotals[key], 0).toFixed(2)
                  }
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function ChartWidget({ widget, reportData, onRemove, onEdit, onMaximize }) {
  // For advanced pivot widgets, delegate to PivotTableWidget
  if (widget.type === 'advancedPivot') {
    return (
      <PivotTableWidget
        widget={widget}
        reportData={reportData}
        onRemove={onRemove}
        onEdit={onEdit}
        onMaximize={onMaximize}
      />
    );
  }

  const chartData = useMemo(() => {
    return processData(reportData, widget.config);
  }, [reportData, widget.config]);

  const renderChart = () => {
    if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          No data available
        </div>
      );
    }

    switch (widget.type) {
      case 'bar':
      case 'stackedBar':
        return <BarChartComponent data={chartData} config={widget.config} />;
      case 'pie':
        return <PieChartComponent data={chartData} config={widget.config} />;
      case 'pivot':
        return <PivotTableComponent data={chartData} config={widget.config} />;
      default:
        return <div>Unknown chart type</div>;
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 cursor-move">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{widget.title}</CardTitle>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" onClick={onMaximize} title="Maximize">
              <Maximize2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onEdit} title="Edit">
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemove} title="Remove">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0">
        {renderChart()}
      </CardContent>
    </Card>
  );
}
